import { useRef, useState } from 'react'
import { useStore, selectActiveClip } from '../store'
import { analyzeStyle, gradeToMatch, styleScore } from '../lib/styleDna'

/** GĐ5 — Style DNA: trích "chất màu" từ video mẫu và áp vào dự án. */
export function StylePanel() {
  const inputRef = useRef<HTMLInputElement>(null)
  const style = useStore((s) => s.style)
  const busy = useStore((s) => s.busy)
  const clip = useStore(selectActiveClip)
  const setStyle = useStore((s) => s.setStyle)
  const setGrade = useStore((s) => s.setGrade)
  const toggleStyleApply = useStore((s) => s.toggleStyleApply)
  const clearStyle = useStore((s) => s.clearStyle)
  const studioAudio = useStore((s) => s.studioAudio)
  const toggleStudioAudio = useStore((s) => s.toggleStudioAudio)
  const setStatus = useStore((s) => s.setStatus)

  const [working, setWorking] = useState(false)
  const [score, setScore] = useState<number | null>(null)

  const onExtract = async (file: File) => {
    if (!clip) {
      setStatus('⚠️ Cần có ít nhất một clip trong dự án trước.')
      return
    }
    setWorking(true)
    const url = URL.createObjectURL(file)
    try {
      setStatus('Phân tích video mẫu…')
      const ref = await analyzeStyle(url, 0, setStatus)
      setStatus('Phân tích clip dự án…')
      const proj = await analyzeStyle(clip.url, clip.duration)
      const grade = gradeToMatch(proj, ref)
      setStyle(file.name, ref, grade)
      setScore(styleScore(proj, ref))
      setStatus(`🎨 Đã trích Style DNA từ "${file.name}" và áp vào dự án.`)
    } catch (e) {
      setStatus(`⚠️ ${(e as Error).message}`)
    } finally {
      URL.revokeObjectURL(url)
      setWorking(false)
    }
  }

  const g = style.grade

  return (
    <div className="logopanel">
      <div className="logopanel-head">🎨 Style DNA — học chất kênh</div>

      {!style.ref ? (
        <>
          <button
            className="btn small"
            disabled={busy || working || !clip}
            onClick={() => inputRef.current?.click()}
          >
            {working ? 'Đang phân tích…' : '🎬 Trích Style DNA từ video mẫu'}
          </button>
          <div className="muted small-note">
            Chọn 1 video "chất kênh" của bạn → AI phân tích màu/độ sáng/tương phản/tông ấm-lạnh và
            áp cho dự án để video mới trông đồng nhất.
          </div>
        </>
      ) : (
        <div className="style-body">
          <div className="style-ref">
            Mẫu: <b>{style.refName}</b>
            {score !== null && <span className="score">Độ giống gốc: {score}%</span>}
          </div>

          <div className="style-stats">
            <span>Sáng {(style.ref.brightness * 100) | 0}%</span>
            <span>Tương phản {(style.ref.contrast * 100) | 0}%</span>
            <span>Bão hòa {(style.ref.saturation * 100) | 0}%</span>
            <span>
              Tông {style.ref.warmth >= 0 ? 'ấm' : 'lạnh'} {Math.abs(style.ref.warmth * 100) | 0}%
            </span>
            <span>Nhịp cắt ~{style.ref.avgShotLen.toFixed(1)}s</span>
          </div>

          <label className="switch">
            <input type="checkbox" checked={style.apply} onChange={toggleStyleApply} />
            Áp Style DNA lên preview & xuất
          </label>

          <div className="ctrl-row">
            <label>Sáng {g.brightness.toFixed(2)}</label>
            <input type="range" min={50} max={180} value={Math.round(g.brightness * 100)}
              onChange={(e) => setGrade({ brightness: Number(e.target.value) / 100 })} />
          </div>
          <div className="ctrl-row">
            <label>Tương phản {g.contrast.toFixed(2)}</label>
            <input type="range" min={50} max={180} value={Math.round(g.contrast * 100)}
              onChange={(e) => setGrade({ contrast: Number(e.target.value) / 100 })} />
          </div>
          <div className="ctrl-row">
            <label>Bão hòa {g.saturate.toFixed(2)}</label>
            <input type="range" min={0} max={200} value={Math.round(g.saturate * 100)}
              onChange={(e) => setGrade({ saturate: Number(e.target.value) / 100 })} />
          </div>
          <div className="ctrl-row">
            <label>Tông ấm-lạnh {g.warmth.toFixed(2)}</label>
            <input type="range" min={-100} max={100} value={Math.round(g.warmth * 100)}
              onChange={(e) => setGrade({ warmth: Number(e.target.value) / 100 })} />
          </div>

          <button className="btn small ghost" disabled={busy} onClick={clearStyle}>
            Xóa Style DNA
          </button>
        </div>
      )}

      <label className="switch" style={{ marginTop: 10 }}>
        <input type="checkbox" checked={studioAudio} onChange={toggleStudioAudio} />
        🎧 Studio audio (lọc ù + nén tiếng khi xuất)
      </label>

      <input
        ref={inputRef}
        type="file"
        accept="video/*"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0]
          if (f) onExtract(f)
          e.target.value = ''
        }}
      />
    </div>
  )
}
